import { reportClientError } from "@/lib/sentry";
import React from "react";
import { track } from "@/lib/analytics/analytics";
import { ANALYTICS_EVENTS } from "@/lib/analytics/events";

interface Props {
  children: React.ReactNode;
  /** What to render instead of the crashed subtree. Defaults to nothing —
   * these are always non-essential ambient widgets (a celebration overlay,
   * a network pill), so disappearing quietly is the right recovery, not a
   * "try again" prompt that would outrank whatever page the user is on. */
  fallback?: React.ReactNode;
  /** Short label for logging, e.g. "StreakCelebrationOverlay". */
  name: string;
}

interface State {
  hasError: boolean;
}

/**
 * Isolates a small, always-mounted global widget (a celebration overlay, a
 * network status pill, a modal that fires from a Context Provider sitting
 * above the router) so a bug in it can never take down the whole app.
 *
 * RouteErrorBoundary already covers everything AppShell renders inside the
 * router tree, but a few widgets — LevelUpOverlay and StreakCelebrationOverlay
 * in particular — are rendered from UserProgressContext, which sits *above*
 * RouterProvider in the component tree. RouteErrorBoundary calls
 * useLocation()/useNavigate() internally, so it cannot be used there; this is
 * the router-independent equivalent, scoped to render nothing on failure
 * instead of a full-page recovery screen.
 */
export class SilentErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    reportClientError(error, "SilentErrorBoundary");
    console.error(`[SilentErrorBoundary:${this.props.name}]`, error, info.componentStack);
    track(ANALYTICS_EVENTS.JS_EXCEPTION, {
      message: error.message,
      name: error.name,
      component_stack: info.componentStack?.slice(0, 500),
      boundary: this.props.name,
    });
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback ?? null;
    }
    return this.props.children;
  }
}

export default SilentErrorBoundary;
